import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import type { PageDto } from '@/lib/pagination';
import type { FlowsheetOut, PatientFlowsheetOut, PregnancyOut } from '../types';

function patientUrl(orgId: string, patientId: string) {
  return `/api/v1/organizations/${orgId}/patients/${patientId}/pregnancies`;
}

function pregnancyUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/pregnancies`;
}

export function usePregnancies(patientId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['pregnancies', orgId, patientId],
    queryFn: async () => {
      const { data } = await clientApi.get<PageDto<PregnancyOut>>(patientUrl(orgId!, patientId!));
      return data;
    },
    enabled: !!orgId && !!patientId,
  });
}

export function usePregnancy(pregnancyId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['pregnancies', orgId, pregnancyId],
    queryFn: async () => {
      const { data } = await clientApi.get<PregnancyOut>(
        `${pregnancyUrl(orgId!)}/${pregnancyId}`,
      );
      return data;
    },
    enabled: !!orgId && !!pregnancyId,
  });
}

export function useCreatePregnancy(patientId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await clientApi.post<PregnancyOut>(
        patientUrl(orgId!, patientId!),
        { ...body, patient_id: patientId },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pregnancies', orgId, patientId] });
    },
  });
}

export function useUpdatePregnancy(pregnancyId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await clientApi.patch<PregnancyOut>(
        `${pregnancyUrl(orgId!)}/${pregnancyId}`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pregnancies', orgId] });
    },
  });
}

export function useDeletePregnancy(pregnancyId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await clientApi.delete(`${pregnancyUrl(orgId!)}/${pregnancyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pregnancies', orgId] });
    },
  });
}

export function useFlowsheet(pregnancyId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['pregnancies', orgId, pregnancyId, 'flowsheet'],
    queryFn: async () => {
      const { data } = await clientApi.get<FlowsheetOut>(
        `${pregnancyUrl(orgId!)}/${pregnancyId}/flowsheet`,
      );
      return data;
    },
    enabled: !!orgId && !!pregnancyId,
  });
}

export function useContextViewTemplates() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['templates', orgId, 'context_view'],
    queryFn: async () => {
      const { data } = await clientApi.get<Record<string, unknown>[]>(
        `/api/v1/organizations/${orgId}/templates`,
        { params: { category: 'context_view' } },
      );
      return data;
    },
    enabled: !!orgId,
  });
}

export function usePatientFlowsheet(
  patientId: string | undefined,
  contextViewTemplateId: string | null | undefined,
) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['patient-flowsheet', orgId, patientId, contextViewTemplateId],
    queryFn: async () => {
      const { data } = await clientApi.get<PatientFlowsheetOut>(
        `/api/v1/organizations/${orgId!}/patients/${patientId!}/flowsheet`,
        { params: { context_view_template_id: contextViewTemplateId! } },
      );
      return data;
    },
    enabled: !!orgId && !!patientId && !!contextViewTemplateId,
  });
}
